import { Box, Stack, Tooltip } from "@mui/material";
import {
    usePortfolioSelection,
    usePortfolioManagement,
    usePortfolioProfile,
} from "../../../shared/contexts/portfolio-context/components/PortfolioContextProvider";
import { PortfolioModel } from "../../portfolio-info/types/PortfolioModel";
import CompareIcon from '@mui/icons-material/Compare';
import { ToolsPanelToggleButton } from "../../positions-and-orders/controls";
import { ApplicationSelect } from "../../../shared/components/application-select";

export function ComparedPortfolioSelector() {
    const {
        underlyingAssetPortfolios,
        selectedPortfolio,
    } = usePortfolioSelection();
    const {
        saveSelectedPortfolio,
    } = usePortfolioManagement();
    const {
        selectedComparedPortfolio,
        setSelectedComparedPortfolioByName,
        updateSelectedPortfolio,
    } = usePortfolioProfile();

    const availablePortfoliosForComparing = underlyingAssetPortfolios
        .filter(x => x.id !== selectedPortfolio?.id);
    
    const setComparedPortfolioHandler = async (portfolioModel: PortfolioModel): Promise<void> => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }
        
        selectedPortfolio.portfolioSettings.comparedPortfolioId = portfolioModel.id;
        selectedPortfolio.portfolioSettings.useComparedPortfolio = true;
        updateSelectedPortfolio();
        await setSelectedComparedPortfolioByName(portfolioModel);
        saveSelectedPortfolio();
    }

    const handleUseComparedPortfolioChanging = (): void => {
        if (selectedPortfolio === null || selectedPortfolio.portfolioSettings === null) {
            return;
        }

        selectedPortfolio.portfolioSettings.useComparedPortfolio = !selectedPortfolio.portfolioSettings.useComparedPortfolio;
        updateSelectedPortfolio();
        saveSelectedPortfolio();
    }

    async function handleSelection(value: string) {
        const selectedPortfolioId = Number(value);
        const selectedPortfolioModel = underlyingAssetPortfolios.find(x => x.id === selectedPortfolioId);

        if (selectedPortfolioModel === undefined) {
            return;
        }

        await setComparedPortfolioHandler(selectedPortfolioModel);
    }
    
    const selectorMaxWidth = 300;
    
    return (
        <Stack
            direction="row"
            sx={{
                flex: '1 1 auto',
                minWidth: 0,
                maxWidth: `${selectorMaxWidth}px`,
            }}
            spacing={0.5}
        >
            <ToolsPanelToggleButton
                isActivated={selectedPortfolio?.portfolioSettings?.useComparedPortfolio ?? false}
                setIsActivated={handleUseComparedPortfolioChanging}
                tooltipTextWhenActivated="Hide compared portfolio"
                tooltipTextWhenDeactivated="Show compared portfolio"
            >
                <CompareIcon sx={{
                    height: '18px',
                    width: '18px',
                }} />
            </ToolsPanelToggleButton>
            <ApplicationSelect
                value={selectedComparedPortfolio
                    ? selectedComparedPortfolio.id.toString()
                    : ' '}
                options={availablePortfoliosForComparing.map((portfolio) => ({
                    value: portfolio.id.toString(),
                    label: portfolio.name,
                    content: (
                        <Tooltip
                            placement="left-start"
                            arrow
                            componentsProps={{
                                tooltip: {
                                    sx: {
                                        fontSize: 13,
                                        maxWidth: '500px'
                                    },
                                },
                            }}
                            slotProps={{
                                popper: {
                                    modifiers: [
                                        {
                                            name: 'offset',
                                            options: {
                                                offset: [-5, 3],
                                            },
                                        },
                                    ],
                                },
                            }}
                            title={
                                portfolio.name
                            }
                        >
                            <Box
                                sx={{
                                    minWidth: 0,
                                    maxWidth: `${selectorMaxWidth}px`,

                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {portfolio.name}
                            </Box>
                        </Tooltip>
                    ),
                }))}
                width="100%"
                menuMaxWidth={`${selectorMaxWidth}px`}
                disabled={isPortfolioSelectorDisabled(selectedPortfolio, availablePortfoliosForComparing)}
                fontSize="14px"
                fontWeight={600}
                menuItemPaddingY={0.5}
                formControlSx={{
                    flex: 1,
                    minWidth: 0,
                }}
                onChange={handleSelection}
            />
        </Stack>
    );
}

function isPortfolioSelectorDisabled(
    selectedPortfolio: PortfolioModel | null, portfolios: PortfolioModel[]
): boolean {
    if (portfolios.length === 0 || selectedPortfolio === null || selectedPortfolio.portfolioSettings === null){
        return false;
    }
    
    return !selectedPortfolio!.portfolioSettings!.useComparedPortfolio;
}
